import { database, firebase, dbFirestore } from "../../firebase";
import { v4 as uuid } from "uuid";

export interface Letter {
  id: string;
  attempt: number;
  state: "DOING" | "IN_REVIEW" | "SUCCESS";
  textHTML: string;
  userId: string;
  user: {
    name: string;
    lastname: string;
  };
  location: {
    courseId: string;
    episodeId: string;
    chapterId: string;
    lessonId: string;
  };
  updatedAt: number;
}

export const getLettersToReview = (
  onUpdate: (data: Letter[]) => void
): (() => void) => {
  let unsubscribe = dbFirestore
    .collection("letters")
    .where("state", "==", "IN_REVIEW")
    .orderBy("updatedAt", "desc")
    .onSnapshot((lettersSnapshot) => {
      let letters: Letter[] = [];
      lettersSnapshot.forEach((letterDoc) => {
        const data = letterDoc.data();
        letters.push({
          id: letterDoc.id,
          attempt: data.attempt,
          state: data.state,
          textHTML: data.text,
          updatedAt: (data.updatedAt.toDate() as Date).getTime(),
          userId: data.userId,
          location: {
            courseId: data.location?.courseId,
            episodeId: data.location?.episodeId,
            chapterId: data.location?.chapterId,
            lessonId: data.location?.lessonId,
          },
          user: {
            name: data.user.name,
            lastname: data.user.lastname,
          },
        });
      });
      onUpdate(letters);
    });

  return () => unsubscribe();
};

export interface CheckoutPage {
  id: string;
  videoPromotionURL: string;
  priceTotal: number;
  discountPercentage: number;
  updatedAt?: number;
}

export const getCheckoutPageById = (
  checkoutId: string,
  onUpdate: (data: CheckoutPage) => void
): (() => void) => {
  let unsubscribe = dbFirestore
    .collection("checkoutPages")
    .doc(checkoutId)
    .onSnapshot((checkoutPageSnapshot) => {
      const data = checkoutPageSnapshot.data();
      if (data !== undefined)
        onUpdate({
          id: checkoutId,
          videoPromotionURL: data.videoPromotionURL,
          priceTotal: data.priceTotal,
          discountPercentage: data.discountPercentage,
          updatedAt:
            data.updatedAt !== null
              ? (data.updatedAt.toDate() as Date).getTime()
              : undefined,
        });
    });

  return () => unsubscribe();
};

export const getCheckoutPages = (
  onUpdate: (data: CheckoutPage[]) => void
): (() => void) => {
  let unsubscribe = dbFirestore
    .collection("checkoutPages")
    .orderBy("updatedAt", "desc")
    .onSnapshot((checkoutPagesSnapshot) => {
      let checkoutPages: CheckoutPage[] = [];
      checkoutPagesSnapshot.forEach((checkoutPage) => {
        const data = checkoutPage.data();
        checkoutPages.push({
          id: checkoutPage.id,
          videoPromotionURL: data.videoPromotionURL,
          priceTotal: data.priceTotal,
          discountPercentage: data.discountPercentage,
          updatedAt:
            data.updatedAt !== null
              ? (data.updatedAt.toDate() as Date).getTime()
              : undefined,
        });
      });
      onUpdate(checkoutPages);
    });

  return () => unsubscribe();
};

export const updateCheckoutPage = async (
  checkoutId: string,
  data: {
    videoPromotionURL: string;
    priceTotal: number;
    discountPercentage: number;
  }
): Promise<void> => {
  await dbFirestore
    .collection("checkoutPages")
    .doc(checkoutId)
    .set(
      { ...data, updatedAt: firebase.firestore.FieldValue.serverTimestamp() },
      { merge: true }
    );
};

export const deleteCheckoutPage = async (checkoutId: string): Promise<void> => {
  await dbFirestore.collection("checkoutPages").doc(checkoutId).delete();
};
